import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Loading } from '@/components/ui/daisy';
import useAssessments from '@/data/hook/evaluation/useAssessments';
import useMasterEvaluations from '@/data/hook/evaluation/useMasterEvaluations';
import useSlaveAssessments from '@/data/hook/evaluation/useSlaveAssessments';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { getNextOrder } from '@/utils/list';
import alert from '@/utils/shared/alert';
import AssessmentForm, { AssessmentFormFields } from './AssessmentForm';

type AssessmentFormServiceProps = {
  evaluation: Evaluation;
  assessment?: Assessment;
  courseLineId: number;
  onClose: () => void;
};

export default function AssessmentFormService({
  evaluation,
  assessment,
  courseLineId,
  onClose,
}: AssessmentFormServiceProps) {
  const { evaluationsMap, cacheManager } = useMasterEvaluations({
    courseLineId,
  });

  const [createdAssessment, setCreatedAssessment] = useState<Assessment>();

  const evaluationFromState = evaluationsMap[evaluation.id] ?? evaluation;
  const baseAssessment = assessment ?? createdAssessment;
  const assessmentFromState =
    evaluationFromState.assessments?.find(a => a.id === baseAssessment?.id) ??
    baseAssessment;

  const { createAssessment, updateAssessment } = useAssessments({
    evaluation: evaluationFromState,
    cacheManager,
  });

  const { slaveEntries, isLoading, createSlaves, updateSlaves } =
    useSlaveAssessments({ evaluation: evaluationFromState });

  const handleCreate = async (data: AssessmentFormFields) => {
    const newAssessment = await createAssessment({
      name: data.name,
      description: data.description,
      maxGrade: data.maxGrade,
      order: getNextOrder(evaluationFromState.assessments ?? []),
    });
    setCreatedAssessment(newAssessment);
    await createSlaves(newAssessment, data.lessonBlocksBySlave);
  };

  const handleUpdate = async (data: AssessmentFormFields) => {
    if (!assessmentFromState)
      throw new Error('Assessment is required for update');

    const dataChanges = buildChangedObject(assessmentFromState, {
      name: data.name,
      description: data.description,
      maxGrade: data.maxGrade,
    });

    if (Object.keys(dataChanges).length > 0) {
      await updateAssessment({
        assessmentId: assessmentFromState.id,
        data: dataChanges,
      });
    }

    await updateSlaves(assessmentFromState, data.lessonBlocksBySlave);
  };

  const handleSubmit = async (data: AssessmentFormFields) => {
    if (!assessmentFromState) return handleCreate(data);
    else return handleUpdate(data);
  };

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: handleSubmit,
    onError: err => alert.error(getErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading variant="spinner" size="lg" color="primary" />
      </div>
    );
  }

  return (
    <AssessmentForm
      evaluation={evaluationFromState}
      assessment={assessmentFromState}
      cacheManager={cacheManager}
      slaveEntries={slaveEntries}
      onSubmit={onSubmit}
      onCancel={onClose}
      isLoading={isPending}
    />
  );
}
